/**
 * Pure ICD-10-CM code utilities. Kept as a leaf module (no hook/config/service
 * imports) so it can be used from `~/common/converters` without creating a
 * circular import via `~/hooks/useICD10Codes`.
 */
import type { ICD10Code } from "~/types/icd10";

/**
 * Format an ICD-10 code for display in dropdown/selection
 * e.g., "G56.01 - Carpal tunnel syndrome, right upper limb"
 */
export function formatICD10Display(code: ICD10Code): string {
  return `${code.codeFormatted} - ${code.description}`;
}

/**
 * Normalize an ICD-10-CM code to its canonical raw (dot-less) form.
 * Trims whitespace, uppercases, strips any dots. Idempotent.
 * e.g., " z04.2 " -> "Z042", "G56.01" -> "G5601", "G5601" -> "G5601"
 */
export function toRawICD10Code(rawCode: string): string {
  return rawCode.trim().toUpperCase().replace(/\./g, "");
}

/**
 * Insert the canonical dot after position 3 in an ICD-10-CM code.
 * Idempotent — already-formatted codes pass through unchanged.
 * e.g., "G5601" -> "G56.01", " z042 " -> "Z04.2", "G56.01" -> "G56.01"
 */
export function formatICD10Code(rawCode: string): string {
  const cleaned = toRawICD10Code(rawCode);
  if (cleaned.length <= 3) return cleaned;
  return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
}

/**
 * Find a code by its raw code string
 */
export function findCodeByRaw(
  codes: ICD10Code[],
  rawCode: string,
): ICD10Code | undefined {
  return codes.find((c) => c.code === rawCode);
}
